import { Controller } from "@hotwired/stimulus"

// Mini icon-view sidebar still uses class toggling — those rows have no
// nested children to leak the wash through, so a single-class approach is
// fine. Expanded rows toggle `data-active` so Tailwind variants
// (`group-data-[active=true]/card:...`) can target the body link without
// covering the children container of a multi-session group.
const MINI_ACTIVE = ["ring-2", "ring-vivid-purple"]

export default class extends Controller {
  static targets = ["card"]
  static values = { selected: String }

  connect() {
    if (this.hasSelectedValue && this.selectedValue) {
      this.highlight(this.selectedValue)
    }

    this._handleKeydown = (e) => this.#onKeydown(e)
    window.addEventListener("keydown", this._handleKeydown)
  }

  disconnect() {
    window.removeEventListener("keydown", this._handleKeydown)
  }

  select(event) {
    const eventId = event.params.eventId || event.currentTarget.dataset.eventId
    this.highlight(eventId)
  }

  deselect() {
    this.cardTargets.forEach((card) => this.#deactivate(card))
  }

  highlight(eventId) {
    const id = String(eventId)
    this.cardTargets.forEach((card) => {
      if (card.dataset.eventCardId === id) {
        this.#activate(card)
        this.#expandGroupOf(card)
      } else {
        this.#deactivate(card)
      }
    })
  }

  // Selecting a weekend or one of its children expands the parent group
  // (one-way — chevron is the only way to re-collapse).
  #expandGroupOf(card) {
    const group = card.closest('[data-controller~="event-group"]')
    if (!group) return
    const ctrl = this.application.getControllerForElementAndIdentifier(
      group,
      "event-group",
    )
    if (ctrl) ctrl.expand()
  }

  #isMini(card) {
    return card.dataset.eventSelectMini === "true"
  }

  #activate(card) {
    if (this.#isMini(card)) {
      card.classList.add(...MINI_ACTIVE)
    } else {
      card.dataset.active = "true"
    }
  }

  #deactivate(card) {
    if (this.#isMini(card)) {
      card.classList.remove(...MINI_ACTIVE)
    } else {
      delete card.dataset.active
    }
  }

  #activeCard() {
    return this.cardTargets.find((c) =>
      this.#isMini(c)
        ? c.classList.contains(MINI_ACTIVE[0])
        : c.dataset.active === "true",
    )
  }

  #onKeydown(event) {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return

    // Don't interfere with inputs
    const tag = document.activeElement?.tagName
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return
    if (document.activeElement?.isContentEditable) return

    // Only handle when no lap-list is visible (session detail takes priority)
    const lapList = document.querySelector('[data-controller~="lap-list"]')
    if (lapList && lapList.offsetParent !== null) return

    event.preventDefault()

    const active = this.#activeCard()
    const cards = this.cardTargets
    if (cards.length === 0) return

    const currentIndex = active ? cards.indexOf(active) : -1
    const nextIndex =
      event.key === "ArrowUp"
        ? currentIndex <= 0
          ? 0
          : currentIndex - 1
        : currentIndex < 0
          ? 0
          : Math.min(currentIndex + 1, cards.length - 1)

    const nextCard = cards[nextIndex]
    if (!nextCard || nextCard === active) return

    // Find the primary link in the card and click it to trigger Turbo navigation
    const link = nextCard.querySelector("a[data-turbo-frame]")
    if (link) {
      this.highlight(nextCard.dataset.eventCardId)
      link.click()
      nextCard.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }
  }
}
